/**
 * Assign Supervisor Page - Microsoft 365 Style
 * Assign zone supervisors
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Text,
  Button,
  Input,
  Field,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
} from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { SearchableDropdown, TablePagination } from '../../../components/common';
import { usePagination } from '../../../hooks/usePagination';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { useAppContext } from '../../../contexts/AppContainer';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  formSection: {
    marginBottom: '24px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr auto',
    gap: '20px',
    alignItems: 'end',
  },
  searchBar: {
    marginBottom: '20px',
    marginTop: '24px',
    maxWidth: '300px',
  },
  tableContainer: {
    marginTop: '16px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  emptyStateTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '8px',
  },
  emptyStateText: {
    fontSize: '14px',
    color: '#605e5c',
  },
});

interface Zone {
  id: number;
  name: string;
  description?: string;
  region?: string;
}

interface Department {
  id: string;
  department_name: string;
}

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  department_id?: number;
}

interface Shift {
  id: number;
  name: string;
  start_time: string;
  end_time: string;
}

interface SupervisorAssignment {
  id: number;
  employee_id: number;
  supervisor_id?: number;
  zone_id: number;
  shift_id: number;
  shift_name?: string;
  start_date: string;
  end_date?: string;
  assigned_by?: number;
  date_assigned?: string;
}

export const AssignSupervisorFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { profileData } = useAppContext();

  const [zones, setZones] = useState<Zone[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [assignments, setAssignments] = useState<SupervisorAssignment[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('');
  const [formData, setFormData] = useState({
    id: 0,
    zone_id: 0,
    employee_id: 0,
    shift_id: 0,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
  });

  // Use ref to always access latest form data
  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<SupervisorAssignment | null>(null);

  useEffect(() => {
    fetchDropdownData();
    fetchAssignments();
  }, []);

  const fetchDropdownData = async () => {
    try {
      const [zonesRes, departmentsRes, employeesRes, shiftsRes] = await Promise.all([
        ApiClient.get('/get-zones'),
        ApiClient.get('/get-departments'),
        ApiClient.get('/get-employee-data'),
        ApiClient.get('/get-shifts'),
      ]);

      if (zonesRes.data.status === 'success') {
        setZones(zonesRes.data.data || []);
      }
      if (departmentsRes.data.status === 'success') {
        setDepartments(departmentsRes.data.data || []);
      }
      if (employeesRes.data.status === 'success') {
        setEmployees(employeesRes.data.data || []);
      }
      if (shiftsRes.data.status === 'success') {
        setShifts(shiftsRes.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const fetchAssignments = async () => {
    try {
      const response = await ApiClient.get('/get-assign-supervisors');
      if (response.data.status === 'success') {
        setAssignments(response.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const getFilteredEmployees = () => {
    if (!selectedDepartmentId) {
      return employees;
    }
    return employees.filter(e => e.department_id === Number(selectedDepartmentId));
  };

  const handleDepartmentChange = (departmentId: string) => {
    setSelectedDepartmentId(departmentId);
    setFormData(prev => ({ ...prev, employee_id: 0 }));
  };

  const handleSubmit = useCallback(async () => {
    const currentFormData = formDataRef.current;

    if (!currentFormData.zone_id || !currentFormData.employee_id || !currentFormData.shift_id) {
      newToast('error', 'Zone, Supervisor, and Shift are required');
      return;
    }

    if (!currentFormData.start_date) {
      newToast('error', 'Start date is required');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...(currentFormData.id && { id: currentFormData.id }),
        zone_id: currentFormData.zone_id,
        employee_id: currentFormData.employee_id,
        shift_id: currentFormData.shift_id,
        start_date: currentFormData.start_date,
        ...(currentFormData.end_date && { end_date: currentFormData.end_date }),
        ...(profileData?.employeeId && { assigned_by: Number(profileData.employeeId) }),
      };

      const response = await ApiClient.post('/add-update-assign-supervisor', payload);
      if (response.data.status === 'success') {
        newToast(
          'success',
          isEditing
            ? 'Supervisor assignment updated successfully'
            : 'Supervisor assigned successfully'
        );
        resetForm();
        fetchAssignments();
      } else {
        newToast('error', response.data.message || 'Failed to assign supervisor');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  }, [isEditing, profileData]);

  const resetForm = () => {
    setFormData({
      id: 0,
      zone_id: 0,
      employee_id: 0,
      shift_id: 0,
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
    });
    setSelectedDepartmentId('');
    setIsEditing(false);
  };

  const handleEdit = (assignment: SupervisorAssignment) => {
    // Format dates to YYYY-MM-DD for date input (handles both "2025-01-03" and "2025-01-03 12:00:00" formats)
    const formatDateForInput = (dateStr: string | undefined | null): string => {
      if (!dateStr) {
        return '';
      }
      const datePart = dateStr.split(' ')[0] || '';
      return datePart.split('T')[0] || '';
    };

    setFormData({
      id: assignment.id,
      zone_id: assignment.zone_id,
      employee_id: assignment.employee_id,
      shift_id: assignment.shift_id,
      start_date: formatDateForInput(assignment.start_date),
      end_date: formatDateForInput(assignment.end_date),
    });
    setIsEditing(true);
  };

  const handleView = (assignment: SupervisorAssignment) => {
    navigate(`/account/guard-operations/supervisor-assignment/${assignment.id}`);
  };

  const handleDeleteClick = (assignment: SupervisorAssignment) => {
    setSelectedAssignment(assignment);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAssignment) {
      return;
    }

    try {
      const response = await ApiClient.post('/delete-assign-supervisor', {
        id: selectedAssignment.id,
      });
      if (response.data.status === 'success') {
        newToast('success', 'Supervisor assignment deleted successfully');
        fetchAssignments();
      } else {
        newToast('error', response.data.message || 'Failed to delete assignment');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    } finally {
      setDeleteDialogOpen(false);
      setSelectedAssignment(null);
    }
  };

  const getZoneName = (zoneId: number) => {
    const zone = zones.find(z => z.id === zoneId);
    return zone?.name || `Zone ${zoneId}`;
  };

  const getEmployeeName = (employeeId: number) => {
    const employee = employees.find(e => e.id === employeeId);
    return employee ? `${employee.first_name} ${employee.last_name}` : `Employee ${employeeId}`;
  };

  const getShiftName = (shiftId: number) => {
    const shift = shifts.find(s => s.id === shiftId);
    return shift?.name || `Shift ${shiftId}`;
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) {
      return '-';
    }

    // Handle formats like "2026-01-03" or "2026-01-03 12:00:00"
    const cleanDate = dateString.split(' ')[0] || '';
    const date = new Date(cleanDate);

    if (isNaN(date.getTime())) {
      return '-';
    }

    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const filteredAssignments = assignments.filter(a => {
    const zoneName = getZoneName(a.zone_id).toLowerCase();
    const employeeName = getEmployeeName(a.employee_id).toLowerCase();
    const query = searchQuery.toLowerCase();
    return zoneName.includes(query) || employeeName.includes(query);
  });

  // Pagination
  const { currentPage, itemsPerPage, paginatedData, totalItems, setCurrentPage, setItemsPerPage } =
    usePagination({
      data: filteredAssignments,
      initialPage: 1,
      initialItemsPerPage: 10,
    });

  return (
    <div>
      <div className={styles.pageHeader}>
        <Text className={styles.pageTitle}>Assign Zone Supervisor</Text>
        <Text className={styles.pageSubtitle}>
          {assignments.length} supervisor{assignments.length !== 1 ? 's' : ''} assigned
        </Text>
      </div>

      <ContentCard>
        {/* Form Section */}
        <div className={styles.formSection}>
          {/* Row 1: Zone, Department, Supervisor, Shift */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '20px',
              marginBottom: '16px',
            }}
          >
            <Field label="Zone" required>
              <SearchableDropdown
                placeholder="Select zone"
                options={zones.map(z => ({
                  id: z.id,
                  label: z.name,
                }))}
                selectedValue={formData.zone_id ? String(formData.zone_id) : ''}
                onSelect={value => setFormData(prev => ({ ...prev, zone_id: Number(value) }))}
                required
              />
            </Field>

            <Field label="Department">
              <SearchableDropdown
                placeholder="Select department"
                options={[
                  { id: '', label: 'All Departments' },
                  ...departments.map(d => ({
                    id: d.id,
                    label: d.department_name,
                  })),
                ]}
                selectedValue={selectedDepartmentId}
                onSelect={value => handleDepartmentChange(value)}
              />
            </Field>

            <Field label="Supervisor" required>
              <SearchableDropdown
                placeholder="Select supervisor"
                options={getFilteredEmployees().map(e => ({
                  id: e.id,
                  label: `${e.first_name} ${e.last_name}`,
                }))}
                selectedValue={formData.employee_id ? String(formData.employee_id) : ''}
                onSelect={value => setFormData(prev => ({ ...prev, employee_id: Number(value) }))}
                required
              />
            </Field>

            <Field label="Shift" required>
              <SearchableDropdown
                placeholder="Select shift"
                options={shifts.map(s => ({
                  id: s.id,
                  label: s.name,
                }))}
                selectedValue={formData.shift_id ? String(formData.shift_id) : ''}
                onSelect={value => setFormData(prev => ({ ...prev, shift_id: Number(value) }))}
                required
              />
            </Field>
          </div>

          {/* Row 2: Start Date, End Date, Submit Button */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr auto',
              gap: '20px',
              alignItems: 'end',
            }}
          >
            <Field label="Start Date" required>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e, data) => setFormData(prev => ({ ...prev, start_date: data.value }))}
              />
            </Field>

            <Field label="End Date">
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e, data) => setFormData(prev => ({ ...prev, end_date: data.value }))}
              />
            </Field>

            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={submitting}
              size="large"
              style={{ height: '40px', minWidth: '120px' }}
            >
              {submitting ? 'Submitting...' : isEditing ? 'Update' : 'Submit'}
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        <div className={styles.searchBar}>
          <Input
            placeholder="Search table..."
            value={searchQuery}
            onChange={(e, data) => setSearchQuery(data.value)}
            contentBefore={<SearchRegular />}
            style={{ width: '100%' }}
          />
        </div>

        {/* Assignments Table */}
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th className={styles.th}>Zone</th>
                <th className={styles.th}>Supervisor</th>
                <th className={styles.th}>Shift</th>
                <th className={styles.th}>Start Date</th>
                <th className={styles.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredAssignments.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <div className={styles.emptyState}>
                      <Text className={styles.emptyStateTitle}>No Supervisor Assignments</Text>
                      <Text className={styles.emptyStateText}>
                        Assign supervisors to zones using the form above
                      </Text>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedData.map(assignment => (
                  <tr key={assignment.id} className={styles.tr}>
                    <td className={styles.td}>{getZoneName(assignment.zone_id)}</td>
                    <td className={styles.td}>
                      {getEmployeeName(assignment.supervisor_id || assignment.employee_id)}
                    </td>
                    <td className={styles.td}>{getShiftName(assignment.shift_id)}</td>
                    <td className={styles.td}>{formatDate(assignment.date_assigned)}</td>
                    <td className={styles.td} style={{ width: '150px' }}>
                      <div className={styles.actionButtons}>
                        <Button
                          size="small"
                          appearance="subtle"
                          onClick={() => handleView(assignment)}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          appearance="subtle"
                          onClick={() => handleEdit(assignment)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          appearance="subtle"
                          onClick={() => handleDeleteClick(assignment)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {filteredAssignments.length > 0 && (
            <TablePagination
              totalItems={totalItems}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          )}
        </div>
      </ContentCard>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(e, data) => setDeleteDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <Text>
                Are you sure you want to delete this supervisor assignment?
                {selectedAssignment && (
                  <span style={{ display: 'block', marginTop: '8px', fontWeight: 600 }}>
                    {getEmployeeName(selectedAssignment.employee_id)} -{' '}
                    {getZoneName(selectedAssignment.zone_id)}
                  </span>
                )}
              </Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                style={{ backgroundColor: '#d13438' }}
                onClick={handleDeleteConfirm}
              >
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default AssignSupervisorFluentUI;
